import * as React from "react";
import { Item, Accordion, Grid, Icon, Button, Label } from "semantic-ui-react";

import * as moment from "moment";
import { useDispatch } from "@teamprevent/admin-redux";

import { DropdownOption } from "@teamprevent/common/questionnaires/model";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";
import { Link as UserLink } from "@teamprevent/common/task/taskActions/models";

import { ADMIN_ROLES, Authorize } from "../auth";
import { VideoMeetingsUpper } from "../videoMeeting/components/VideoMeetingsUpper";
import { VideoMeetingsLower } from "../videoMeeting/components/VideoMeetingsLower";
import { ActionableTaskAssign } from "../actionableTasks/components/ActionableTaskAssign";
import { ACTION_TASK_SUCCESS } from "../crud/ohCase/actiontypes";
import { OhCaseAction } from "../crud/ohCase/actions";

import { TaskWithActionsProps } from "./taskWithActions";
import { isImmunisationTask } from "./model";
import { EditTaskCompletionDataButton } from "./taskActions/EditTaskCompletionDataButton";
import { DeleteTaskButton } from "./taskActions/DeleteTaskButton";
import { CheckInButton } from "./taskActions/CheckInButton";

import { Link } from "redux-little-router";

interface TaskProps {
    actions: React.JSX.Element;
    adminUserOptions: DropdownOption[];
    secondaryActions?: React.JSX.Element;
}

type Props = TaskProps & TaskWithActionsProps & { children?: React.ReactNode };

export const Task: React.FunctionComponent<Props> = (props: Props) => {

    const [active, setActive] = React.useState(false);
    const [creatingMeeting, setCreatingMeeting] = React.useState<boolean>(false);
    const [assignModalOpen, setAssignModalOpen] = React.useState<boolean>(false);

    const dispatch = useDispatch();

    const taskStateDescription = React.useMemo((): string => {
        if (props.task.cancelled) {
            return "Cancelled";
        }

        return props.task.completed
            ? `Completed: ${props.task.completedDate?.format("L")}`
            : "Not completed";
    }, [props.task.cancelled, props.task.completed, props.task.completedDate]);

    const onAssignClicked = React.useCallback(() => {
        setAssignModalOpen(true);
    }, []);

    const taskUsersUpdateSuccess = React.useCallback((task: TaskModel): OhCaseAction => {
        return {
            type: ACTION_TASK_SUCCESS,
            payload: { ohCaseId: task.caseId, tasks: [task] }
        };
    }, []);

    const onAssigned = React.useCallback((assignedUsers: UserLink[]) => {
        const updatedTask = { ...props.task, assignedUsers };
        dispatch(taskUsersUpdateSuccess(updatedTask));
        setAssignModalOpen(false);
    }, [dispatch, props.task, taskUsersUpdateSuccess]);

    const onCanceled = React.useCallback(() => setAssignModalOpen(false), []);

    const onTitleClick = React.useCallback(() => setActive(previousValue => !previousValue), []);

    return (
        <>
            <Item className="task">
                <Accordion fluid className="task">
                    <Accordion.Title active={active} onClick={onTitleClick}>
                        <Grid columns={3}>
                            <Grid.Column width="9">{props.task.description}</Grid.Column>
                            <Grid.Column width="3">{taskStateDescription}</Grid.Column>
                            {props.task.appointmentId &&
                                <Grid.Column width="2" floated="right">
                                    <CheckInButton appointmentId={props.task.appointmentId} />
                                </Grid.Column>
                            }
                            <Grid.Column width="2" floated="right">
                                <Authorize roles={[ADMIN_ROLES.SYSTEM_ADMIN]}>
                                    <DeleteTaskButton task={props.task} />
                                </Authorize>
                                {props.task.completed && <EditTaskCompletionDataButton task={props.task} adminUserOptions={props.adminUserOptions} />}
                            </Grid.Column>
                        </Grid>
                    </Accordion.Title>
                    <Accordion.Content active={active}>
                        {props.children && <Item.Extra>{props.children}</Item.Extra>}
                        {isImmunisationTask(props.task) && props.task.immunisationTypeTasks.map(t =>
                            <Item.Extra key={t.id}>{t.name}</Item.Extra>
                        )}
                        <Item.Extra>Created: {props.task.dateCreated.format("L")}</Item.Extra>
                        {props.task.dueDate &&
                            <Item.Extra>
                                {props.task.appointmentId ? `Appointment: ${props.task.dueDate.format("L LT")}` : `Due: ${props.task.dueDate.format("L")}`}
                                <>
                                    &nbsp;
                                    {props.task.appointmentId ?
                                        <>
                                            {/* eslint-disable max-len */}
                                            <Link href={`/calendar/${moment(props.task.dueDate).startOf("day").format(moment.HTML5_FMT.DATE)}?RestrictToAppointmentId=${props.task.appointmentId}`}>
                                                <Icon name="binoculars" />
                                            </Link>
                                            {/* eslint-enable max-len */}
                                        </> :
                                        <Icon name="binoculars" disabled={true} />
                                    }
                                </>
                            </Item.Extra>
                        }
                        {props.task.createdBy && <Item.Extra>Created By: {props.task.createdBy.name}</Item.Extra>}
                        {props.task.completedDate && <Item.Extra>Task Completed Date: {props.task.completedDate.format("L HH:mm")}</Item.Extra>}
                        {props.task.completedBy && <Item.Extra>Task Completed By: {props.task.completedBy.name}</Item.Extra>}
                        {props.task.actualCompletionDate && <Item.Extra>Actual Completion Date: {props.task.actualCompletionDate.format("L")}</Item.Extra>}
                        {props.task.actuallyCarriedOutBy && <Item.Extra>Actually Carried out By: {props.task.actuallyCarriedOutBy.name}</Item.Extra>}
                        {props.task.isBookTask && props.task.completed &&
                            <Item.Extra>Date First Offered: {props.task.earlierAppointmentDate?.format("L")}</Item.Extra>}
                        {props.task.closingNote && <Item.Extra>Closure Note: {props.task.closingNote}</Item.Extra>}
                        {!props.task.completed && props.task.isAssignable &&
                            <>
                                {props.task.assignedUsers && props.task.assignedUsers.length ?
                                    <Item.Extra>
                                        Assigned to {props.task.assignedUsers.map(u => (
                                            <Label key={u.id} size="tiny" color="grey" content={u.name}
                                            />))}
                                    </Item.Extra> :
                                    <Item.Extra>
                                        <Label size="tiny" color="red" content="No-one assigned" />
                                    </Item.Extra>
                                }
                            </>
                        }
                        <Item.Description className="task-notes">{props.task.notes}</Item.Description>
                        <Item.Extra><VideoMeetingsUpper task={props.task} creatingMeeting={creatingMeeting} /></Item.Extra>
                        <Item.Extra className="video-meetings-lower">
                            <Grid columns={2}>
                                <Grid.Column className="left-column">

                                    <VideoMeetingsLower task={props.task} creatingMeeting={creatingMeeting} setCreatingMeeting={setCreatingMeeting} />
                                    {!props.task.completed && props.task.isAssignable &&
                                        <Button
                                            content="Assign"
                                            onClick={onAssignClicked}
                                        />
                                    }
                                    {props.secondaryActions}
                                </Grid.Column>
                                <Grid.Column  className="right-column">
                                    {props.actions}
                                </Grid.Column>
                            </Grid>
                        </Item.Extra>
                    </Accordion.Content>
                </Accordion>
            </Item>
            <ActionableTaskAssign
                tasks={[{ ...props.task, selected: true, caseSubject: "", immunisationTypeTasks: [] }]}
                open={assignModalOpen}
                onSaved={onAssigned}
                onCanceled={onCanceled}
            />
        </>
    );
};
